import { useEffect, useRef, useState } from "react";
import { UserPlus, Upload, Search, Trash2, Download, Users as UsersIcon, UserMinus } from "lucide-react";
import {
  listSubscribers, subscribersStats, addSubscriber, deleteSubscriber,
  uploadSubscribersCsv, exportSubscribersCsvBlob,
} from "@/lib/admin-api";
import { toast } from "sonner";
import { formatMtyDateTimeShort as fmtMTY } from "@/lib/mty-date";

export default function AdminSubscribers() {
  const [list, setList] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", nombre: "", ciudad: "", tipo_espacio: "" });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        listSubscribers({ q: q || undefined, status: status || undefined, limit: 200 }),
        subscribersStats(),
      ]);
      setList(l);
      setStats(s);
    } catch (e) {
      toast.error("Error cargando suscriptores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    try {
      await addSubscriber(form);
      toast.success("Suscriptor agregado");
      setForm({ email: "", nombre: "", ciudad: "", tipo_espacio: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar");
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("¿Eliminar este suscriptor?")) return;
    try {
      await deleteSubscriber(id);
      toast.success("Eliminado");
      load();
    } catch {
      toast.error("Error eliminando");
    }
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await uploadSubscribersCsv(file);
      toast.success(`CSV importado: +${r.added} nuevos, ${r.updated} actualizados, ${r.skipped} omitidos`);
      load();
    } catch (err) {
      toast.error("Error subiendo CSV: " + (err.response?.data?.detail || "formato inválido"));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doExport = async () => {
    try {
      const blob = await exportSubscribersCsvBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `suscriptores_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error exportando");
    }
  };

  return (
    <div className="p-5 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-3">
            <UsersIcon className="w-8 h-8 text-red-600" /> Suscriptores
          </h1>
          <p className="text-neutral-500 text-sm mt-1">Base de datos para email marketing</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowAdd(true)} className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm" data-testid="add-subscriber-btn">
            <UserPlus className="w-4 h-4" /> Agregar
          </button>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-neutral-300 text-neutral-700 hover:bg-neutral-50 cursor-pointer">
            <Upload className="w-4 h-4" /> Subir CSV
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={doUpload} data-testid="upload-csv-input" />
          </label>
          <button onClick={doExport} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-neutral-300 text-neutral-700 hover:bg-neutral-50">
            <Download className="w-4 h-4" /> Exportar
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Kpi label="Total" value={stats.total} icon={UsersIcon} />
        <Kpi label="Activos" value={stats.active} color="text-green-600" />
        <Kpi label="Dados de baja" value={stats.unsubscribed} color="text-neutral-500" icon={UserMinus} />
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-xl p-3 flex flex-wrap gap-3 items-center mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar email o nombre" className="form-input pl-9 w-full" data-testid="subscribers-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input">
          <option value="">Todos los estados</option>
          <option value="active">Activos</option>
          <option value="unsubscribed">Dados de baja</option>
        </select>
        <button onClick={load} className="btn-primary px-4 py-2.5 rounded-lg text-sm">Filtrar</button>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-neutral-500">Cargando...</div>
        ) : list.items.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">
            <p>Aún no hay suscriptores.</p>
            <p className="text-xs mt-2">Usa "Agregar" o "Subir CSV" para empezar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="subscribers-table">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Nombre</th>
                  <th className="text-left px-4 py-3">Ciudad</th>
                  <th className="text-left px-4 py-3">Tipo espacio</th>
                  <th className="text-left px-4 py-3">Fuente</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Suscripción</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((s) => (
                  <tr key={s.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-3 font-medium">{s.email}</td>
                    <td className="px-4 py-3">{s.nombre || "—"}</td>
                    <td className="px-4 py-3">{s.ciudad || "—"}</td>
                    <td className="px-4 py-3">{s.tipo_espacio || "—"}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-neutral-100 rounded">{s.source || "—"}</span></td>
                    <td className="px-4 py-3">
                      {s.status === "active" ? (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-semibold">Activo</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-neutral-200 text-neutral-700 rounded-full">Baja</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">{fmtMTY(s.subscribed_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => doDelete(s.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" aria-label="Eliminar" data-testid={`del-sub-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-black section-title mb-4">Agregar suscriptor</h3>
            <form onSubmit={submitAdd} className="space-y-3">
              <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="form-input w-full" data-testid="add-sub-email" />
              <input placeholder="Nombre (opcional)" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="form-input w-full" />
              <input placeholder="Ciudad (opcional)" value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} className="form-input w-full" />
              <input placeholder="Tipo de espacio (opcional)" value={form.tipo_espacio} onChange={(e) => setForm({ ...form, tipo_espacio: e.target.value })} className="form-input w-full" />
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 px-4 py-2.5 rounded-lg text-sm border border-neutral-300 hover:bg-neutral-50">Cancelar</button>
                <button type="submit" className="flex-1 btn-primary px-4 py-2.5 rounded-lg text-sm" data-testid="add-sub-submit">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color = "text-neutral-900" }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />}{label}
      </div>
      <div className={`mt-1 text-3xl font-black ${color}`}>{value}</div>
    </div>
  );
}
